/** Regenerate summary icon button — spinner when pending, tooltip for disabled states */

import { useEffect, useState } from 'react'
import { RefreshCw } from 'lucide-react'
import { cn } from '../../lib/utils'
import { useT, type TranslateFn } from '../../i18n'

interface RegenerateSummaryButtonProps {
  /** File extraction status — button disabled if not 'completed' */
  status: string | null
  /** Epoch ms of last summary regen (for cooldown computation), or null */
  lastSummaryAt: number | null
  /** True while regenerate mutation is in-flight */
  pending: boolean
  onClick: () => void
}

function getTooltip(t: TranslateFn, status: string | null, cooldownSeconds: number, pending: boolean): string {
  if (pending) return t('storage.summary.regenerating')
  if (status !== 'completed') return t('storage.summary.waitForExtraction')
  if (cooldownSeconds > 0) return t('storage.summary.cooldown', { seconds: cooldownSeconds })
  return t('storage.summary.regenerate')
}

/** Compute remaining cooldown seconds given an epoch-ms timestamp */
export function computeCooldownSeconds(lastSummaryAt: number | null, cooldownMs = 60_000): number {
  if (!lastSummaryAt) return 0
  const elapsed = Date.now() - lastSummaryAt
  return Math.max(0, Math.ceil((cooldownMs - elapsed) / 1000))
}

export function RegenerateSummaryButton({
  status,
  lastSummaryAt,
  pending,
  onClick,
}: RegenerateSummaryButtonProps) {
  const t = useT()
  // Tick down once per second while cooldown is active so the tooltip updates
  // without waiting for an external refetch.
  const [cooldownSeconds, setCooldownSeconds] = useState(() => computeCooldownSeconds(lastSummaryAt))

  useEffect(() => {
    setCooldownSeconds(computeCooldownSeconds(lastSummaryAt))
    if (!lastSummaryAt) return
    const id = setInterval(() => {
      const remaining = computeCooldownSeconds(lastSummaryAt)
      setCooldownSeconds(remaining)
      if (remaining === 0) clearInterval(id)
    }, 1000)
    return () => clearInterval(id)
  }, [lastSummaryAt])

  const isDisabled = pending || status !== 'completed' || cooldownSeconds > 0
  const tooltip = getTooltip(t, status, cooldownSeconds, pending)

  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick() }}
      disabled={isDisabled}
      title={tooltip}
      aria-label={tooltip}
      className={cn(
        'shrink-0 rounded-md p-1 transition-colors',
        isDisabled ? 'cursor-not-allowed text-fg-subtle opacity-60' : 'cursor-pointer text-fg-muted hover:bg-surface-hover hover:text-fg',
      )}
    >
      <RefreshCw className={cn('h-3.5 w-3.5', pending && 'animate-spin')} aria-hidden="true" />
    </button>
  )
}
